import React, {useState, useEffect, useMemo, lazy, Suspense} from 'react';
import * as Sentry from "@sentry/react";
import DesktopLayout from './Layouts/Desktop';
import MobileLayout from './Layouts/Mobile';
import TabletLayout from './Layouts/Tablet';
import { Navigate, RouterProvider, createBrowserRouter, useParams } from 'react-router-dom';
import Guest from './Layouts/Guest';
import Login from './Pages/Auth/Login';
import Logout from './Pages/Auth/Logout';
import Register from './Pages/Auth/Register';
import ForgotPassword from './Pages/Auth/ForgotPassword';
import ToastsWrapper from './Components/Toasts';
import Home from './Pages/Home';
import Session, { useSession } from './Components/Session';
import Authenticating from './Pages/Auth/Authenticating';
import RedirectShortLink from './Pages/RedirectShortLink';
import SocketWrapper, { useSocketListener } from './Components/Socket';
import { getAppShellLayout, isLocal } from './Services/helpers';
import ErrorBoundary from './Components/Containers/ErrorBoundary/ErrorBoundary';
import DB from './Data/DB';
import State from './Data/State';
import ServiceWorker from './Services/ServiceWorker';
import Offline from './Components/Offline';

const AdditionalUsers = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Business/AdditionalUsers'));
const Announcements = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Announcements'));
const BankAccount = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Payments/BankAccount'));
const BankReconciliation = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Payments/BankReconciliation'));
const Billing = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Billing'));
const Broadcast = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Broadcast'));
const Broadcasts = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Broadcasts'));
const Calendar = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Calendar'));
const ChangeType = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/ChangeType'));
const Checklist = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Help/Checklist'));
const CloseAccount = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/User/CloseAccount'));
const CommunicationPreferences = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/CommunicationPreferences'));
const Content = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/Content'));
const CustomDomain = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/CustomDomain'));
const Customer = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Customer'));
const CustomerNotifications = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Customers/Notifications'));
const Customers = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Customers'));
const CustomSMSName = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Business/CustomSMSName'));
const Details = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Business/Details'));
const Document = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Document'));
const Documents = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Documents'));
const DocumentTemplates = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Customers/DocumentTemplates'));
const EditService = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/PricingAndServices/Services/Edit'));
const Expenses = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Expenses'));
const Experimental = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Experimental'));
const ExpensesReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Expenses'));
const ForecastReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Forecast'));
const Help = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Help'));
const Imports = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Imports'));
const Invoices = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Invoices'));
const InvoicesReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Invoices'));
const Itemisation = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Itemisation'));
const Jobs = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Jobs'));
const JobsReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Jobs'));
const Leads = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Leads'));
const Links = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/Links'));
const MessageTemplate = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Customers/MessageTemplate'));
const MessageTemplates = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Customers/MessageTemplates'));
const Triggers = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Triggers'));
const Messages = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Messages'));
const More = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/More'));
const MoreRedesign = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/MoreRedesign'));
const Notifications = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/User/Notifications'));
const Overview = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Overview'));
const PaymentOptions = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Payments/PaymentOptions'));
const Payments = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Payments'));
const PaymentsReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Payments'));
const PaymentRepairRedirect = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Payments/PaymentRepairRedirect'));
const ServicesReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Services'));
const ProfitAndLoss = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/ProfitAndLoss'));
const Payouts = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Payments/Payouts'));
const Plan = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Billing/Plan'));
const PricingAndServices = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/PricingAndServices'));
const Services = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Business/Services'));
const Pricing = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Business/Pricing'));
const Profile = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/User/Profile'));
const Security = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/User/Security'));
const Question = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Help/Question'));
const Quote = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Quote'));
const QuoteOptions = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Customers/QuoteOptions'));
const Quotes = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Quotes'));
const ReferAFriend = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/ReferAFriend'));
const Reports = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports'));
const ResetAccount = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/User/ResetAccount'));
const ResetPassword = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Auth/ResetPassword'));
const Roadmap = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Roadmap'));
const Round = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Round'));
const Rounds = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Rounds'));
const RoundsReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Rounds'));
const Setup = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Payments/Setup'));
const Socials = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Socials'));
const Stats = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/Stats'));
const Tags = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Customers/Tags'));
const Theme = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/Theme'));
const ThirdPartySettings = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/ThirdPartySettings'));
const Verification = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Verification'));
const Videos = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Help/Videos'));
const Voip = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Voip'));
const WhatsNew = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/WhatsNew'));
const Work = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Work'));
const QuestionnaireComplete = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Questionnaires/Complete'));
const PerkView = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Perks/View'));
const TimeTrackingReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/TimeTracking'));
const WorkersReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Workers'));
const WorkSummaryReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/WorkSummary'));
const WebsiteReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Website'));
const RecurringRevenueReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/RecurringRevenue'));
const CustomerGrowthReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/CustomerGrowth'));
const Exports = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Exports'));
const HMRC = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/HMRC'));
const IntegrationOAuth = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Integrations/OAuth'));
const ApiAuthorisations = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Integrations'));
const Builder = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Builder/TheBuilder'));

// Admin
const Admin = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Admin'));
const Businesses = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Businesses/index'));
const Revenue = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Revenue'));
const Dial = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Dial'));
const Experiments = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Experiments/index'));
const ScratchPad = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/ScratchPad'));
const Conversations = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Conversations'));
const ConversationsRouter = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Conversations/ConversationsRouter'));
const Assets = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Assets'));
const Assistant = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Assistant'));
const FollowUps = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/FollowUps/FollowUps'));
const AdminReports = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/AdminReports'));
const RiskAssessmentAccuracy = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/RiskAssessmentAccuracy'));
const Lessons = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Lessons'));
const Feedback = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Feedback'));
const FraudulentRiskPayments = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/FraudulentRiskPayments'));
const Shipments = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Shipments'));
const Questionnaires = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Questionnaires'));
const QuestionnaireView = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Questionnaires/View'));
const AdminTemplates = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Templates'));
const AdminPerks = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Perks'));
const AdminPerksGiven = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Perks/Given'));

const allowOfflineLocally = true;

const App = () => {

    const [waitingForCss, setWaitingForCss] = useState(true);
    const [waitingForDB, setWaitingForDB] = useState(ServiceWorker.isEnabled() || isLocal());
    const [appShellLayout, setAppShellLayout] = useState(getAppShellLayout());

    useEffect(() => {
        if (!ServiceWorker.isEnabled() && !(isLocal() && allowOfflineLocally)) {
            setWaitingForDB(false);
            return;
        }

        DB.init().then(() => {
            DB.getAllData().then((data) => {
                State.putAll(data);
                setWaitingForDB(false);
            }).catch((error) => {
                console.error('Error initializing DB', error);
                setWaitingForDB(false);
            });
        }).catch((error) => {
            console.error('Error initializing DB', error);
            setWaitingForDB(false);
        });
    }, []);

    useEffect(() => {
        setWaitingForCss(false);
        document.getElementById('loading-container')?.remove();
    }, []);

    useEffect(() => {
        const resize = () => setAppShellLayout(getAppShellLayout());

        window.addEventListener('resize', resize);

        return () => window.removeEventListener('resize', resize);
    }, []);

    if (waitingForCss || waitingForDB) {
        return null;
    }

    return (
        <ErrorBoundary fullPage={true}>
            <ToastsWrapper>
                <Offline>
                    <Session>
                        <SocketWrapper>
                            <MakeRouter appShellLayout={appShellLayout} />
                            <NotificationSubscription />
                            <BusinessSubscription />
                        </SocketWrapper>
                    </Session>
                </Offline>
            </ToastsWrapper>
        </ErrorBoundary>
    )
}

export default Sentry.withProfiler(App);

const NotificationSubscription = () => {
    const {journal, fetchNotifications, setNotificationBadge} = useSession();

    useSocketListener(
        journal ? {entity: {entityModel: 'Notifications/Journal', entityId: journal.journalId}} : null,
        (data) => {
            setNotificationBadge(data.unreadNotifications);
            fetchNotifications();
        },
    );

    return null;
}

const BusinessSubscription = () => {
    const {business, fetchDetails} = useSession();

    useSocketListener(
        business ? {entity: {entityModel: 'Businesses/Business', entityId: business.id}} : null,
        () => fetchDetails(),
    );

    return null;
}

const MakeRouter = ({ appShellLayout }) => {
    const {hasToken, isReady, showRedesign} = useSession();

    const router = useMemo(() => {
        if (hasToken && isReady) {
            return makeAuthedRouter(appShellLayout, showRedesign);
        }

        return makeGuestRouter(hasToken);
    }, [hasToken, isReady, appShellLayout, showRedesign]);

    return (
        <RouterProvider
            key={`${hasToken}-${isReady}-${appShellLayout}-${showRedesign}`}
            router={router}
        />
    );
}

/**
 * Router Configuration
 *
 * This function defines all routes for the authenticated application.
 * When adding, removing, or modifying routes:
 * - Update Components/GlobalSearch/pages.js if the page should be searchable
 * - Update MoreRedesign.jsx if the page appears in settings (redesign)
 * - Update More.jsx if the page appears in settings (legacy)
 * - Update NavRedesign.jsx if the page appears in navigation
 */

const shellLayoutElement = (layout) => {
    if (layout === 'desktop') {
        return <DesktopLayout />;
    }
    if (layout === 'tablet') {
        return <TabletLayout />;
    }
    return <MobileLayout />;
};

const makeAuthedRouter = (appShellLayout, showRedesign) => {
    return createBrowserRouter([
        {
            path: '/',
            element: shellLayoutElement(appShellLayout),
            children: [
                {
                    index: true,
                    element: <Home />,
                },
                {
                    path: '/work',
                    element: <Navigate to={'/work/'+(new Date().toISOString().slice(0, 10))} />,
                },
                {
                    path: '/work/:date',
                    element: <Suspense><Work /></Suspense>,
                },
                {
                    path: '/plan/calendar',
                    element: <Suspense><Calendar /></Suspense>,
                },
                {
                    path: '/plan/calendar/:week',
                    element: <Suspense><Calendar /></Suspense>,
                },
                {
                    path: '/plan/rounds',
                    element: <Suspense><Rounds /></Suspense>,
                },
                {
                    path: '/plan/rounds/:roundId',
                    element: <Suspense><Round /></Suspense>,
                },
                {
                    path: '/plan/jobs',
                    element: <Suspense><Jobs /></Suspense>,
                },
                {
                    path: '/plan/leads',
                    element: <Suspense><Leads /></Suspense>,
                },
                {
                    path: '/plan/leads/:leadId',
                    element: <Suspense><Leads /></Suspense>,
                },
                {
                    path: '/manage/customers',
                    element: <Suspense><Customers /></Suspense>,
                },
                {
                    path: '/manage/customers/:customerId',
                    element: <Suspense><Customer /></Suspense>
                },
                {
                    path: '/messages',
                    element: <Suspense><Messages /></Suspense>
                },
                {
                    path: '/manage/quotes',
                    element: <Suspense><Quotes /></Suspense>
                },
                {
                    path: '/manage/quotes/:quoteId',
                    element: <Suspense><Quote /></Suspense>
                },
                {
                    path: '/manage/:itemType/:itemId/itemisations/:itemisationId',
                    element: <Suspense><Itemisation /></Suspense>
                },
                {
                    path: '/manage/payments',
                    element: <Suspense><Payments /></Suspense>
                },
                {
                    path: '/manage/payments/:paymentId',
                    element: <Suspense><Payments /></Suspense>
                },
                {
                    path: '/manage/invoices',
                    element: <Suspense><Invoices /></Suspense>
                },
                {
                    path: '/manage/expenses',
                    element: <Suspense><Expenses /></Suspense>
                },
                {
                    path: '/more/broadcasts',
                    element: <Suspense><Broadcasts /></Suspense>
                },
                {
                    path: '/more/broadcasts/:broadcastId',
                    element: <Suspense><Broadcast /></Suspense>
                },
                {
                    path: '/more/experimental',
                    element: <Suspense><Experimental /></Suspense>
                },
                {
                    path: '/more/experimental/:rnd',
                    element: <Suspense><Experimental /></Suspense>
                },
                {
                    path: '/more',
                    element: <Suspense>{showRedesign ? <MoreRedesign /> : <More />}</Suspense>
                },
                {
                    path: '/settings',
                    element: <Suspense>{showRedesign ? <MoreRedesign /> : <More />}</Suspense>
                },
                {
                    path: '/announcements',
                    element: <Suspense><Announcements /></Suspense>
                },
                {
                    path: '/business/details',
                    element: <Suspense><Details /></Suspense>
                },
                {
                    path: '/business/custom-sms-name',
                    element: <Suspense><CustomSMSName /></Suspense>
                },
                {
                    path: '/business/services',
                    element: <Suspense><Services /></Suspense>
                },
                {
                    path: '/business/services/:serviceId',
                    element: <Suspense><EditService /></Suspense>
                },
                {
                    path: '/business/pricing',
                    element: <Suspense><Pricing /></Suspense>
                },
                {
                    path: '/business/services-and-pricing',
                    element: <Suspense><PricingAndServices /></Suspense>
                },
                {
                    path: '/business/documents',
                    element: <Suspense><Documents /></Suspense>,
                },
                {
                    path: '/business/documents/:documentId',
                    element: <Suspense><Document /></Suspense>,
                },
                {
                    path: '/business/additional-users',
                    element: <Suspense><AdditionalUsers /></Suspense>
                },
                {
                    path: '/calls',
                    element: <Suspense><Voip /></Suspense>
                },
                {
                    path: '/user/profile',
                    element: <Suspense><Profile /></Suspense>
                },
                {
                    path: '/user/security',
                    element: <Suspense><Security /></Suspense>
                },
                {
                    path: '/verification',
                    element: <Suspense><Verification /></Suspense>
                },
                {
                    path: '/user/notification-preferences',
                    element: <Suspense><Notifications /></Suspense>
                },
                {
                    path: '/user/close-account',
                    element: <Suspense><CloseAccount /></Suspense>
                },
                {
                    path: '/user/reset-account',
                    element: <Suspense><ResetAccount /></Suspense>
                },
                {
                    path: '/settings/customer-notifications',
                    element: <Suspense><CustomerNotifications /></Suspense>
                },
                {
                    path: '/settings/message-templates/:templateId',
                    element: <Suspense><MessageTemplate /></Suspense>
                },
                {
                    path: '/triggers',
                    element: <Suspense><Triggers /></Suspense>
                },
                {
                    path: '/settings/document-templates',
                    element: <Suspense><DocumentTemplates /></Suspense>
                },
                {
                    path: '/settings/quote-options',
                    element: <Suspense><QuoteOptions /></Suspense>
                },
                {
                    path: '/settings/message-templates',
                    element: <Suspense><MessageTemplates /></Suspense>
                },
                {
                    path: '/settings/import',
                    element: <Suspense><Imports /></Suspense>
                },
                {
                    path: '/settings/integrations',
                    element: <Suspense><ApiAuthorisations /></Suspense>
                },
                {
                    path: '/settings/tags',
                    element: <Suspense><Tags /></Suspense>
                },
                {
                    path: '/settings/payments/setup',
                    element: <Suspense><Setup /></Suspense>
                },
                {
                    path: '/settings/payments/bank-account',
                    element: <Suspense><BankAccount /></Suspense>
                },
                {
                    path: '/settings/payments/options',
                    element: <Suspense><PaymentOptions /></Suspense>
                },
                {
                    path: '/settings/payments/bank-reconciliation',
                    element: <Suspense><BankReconciliation /></Suspense>
                },
                {
                    path: '/settings/payments/payouts',
                    element: <Suspense><Payouts /></Suspense>
                },
                {
                    path: '/settings/payments/repair',
                    element: <Suspense><PaymentRepairRedirect /></Suspense>,
                },
                {
                    path: '/website/type',
                    element: <Suspense><ChangeType /></Suspense>,
                },
                {
                    path: '/website/theme',
                    element: <Suspense><Theme /></Suspense>,
                },
                {
                    path: '/website/links',
                    element: <Suspense><Links /></Suspense>,
                },
                {
                    path: '/website/third-party-settings',
                    element: <Suspense><ThirdPartySettings /></Suspense>,
                },
                {
                    path: '/website/content',
                    element: <Suspense><Content /></Suspense>,
                },
                {
                    path: '/website/custom-domain',
                    element: <Suspense><CustomDomain /></Suspense>,
                },
                {
                    path: '/website/stats',
                    element: <Suspense><Stats /></Suspense>,
                },
                {
                    path: '/builder',
                    element: <Suspense><Builder /></Suspense>
                },
                {
                    path: '/builder/:id',
                    element: <Suspense><Builder /></Suspense>
                },
                {
                    path: '/billing',
                    element: <Suspense><Billing /></Suspense>
                },
                {
                    path: '/billing/plan',
                    element: <Suspense><Plan /></Suspense>
                },
                {
                    path: '/help/videos',
                    element: <Suspense><Videos /></Suspense>
                },
                {
                    path: '/help/checklist',
                    element: <Suspense><Checklist /></Suspense>
                },
                {
                    path: '/help',
                    element: <Suspense><Help /></Suspense>
                },
                {
                    path: '/help/question',
                    element: <Suspense><Question /></Suspense>
                },
                {
                    path: '/help/question/:question',
                    element: <Suspense><Question /></Suspense>
                },
                {
                    path: '/questionnaires/:questionnaireId',
                    element: <Suspense><QuestionnaireComplete /></Suspense>,
                },
                {
                    path: '/perks/:perkGivenId',
                    element: <Suspense><PerkView /></Suspense>,
                },
                {
                    path: '/reports',
                    element: <Suspense><Reports /></Suspense>,
                },
                {
                    path: '/reports/overview',
                    element: <Suspense><Overview /></Suspense>,
                },
                {
                    path: '/reports/jobs',
                    element: <Suspense><JobsReport /></Suspense>,
                },
                {
                    path: '/reports/work-summary',
                    element: <Suspense><WorkSummaryReport /></Suspense>,
                },
                {
                    path: '/reports/workers',
                    element: <Suspense><WorkersReport /></Suspense>,
                },
                {
                    path: '/reports/invoices',
                    element: <Suspense><InvoicesReport /></Suspense>,
                },
                {
                    path: '/reports/rounds',
                    element: <Suspense><RoundsReport /></Suspense>,
                },
                {
                    path: '/reports/expenses',
                    element: <Suspense><ExpensesReport /></Suspense>,
                },
                {
                    path: '/reports/payments',
                    element: <Suspense><PaymentsReport /></Suspense>,
                },
                {
                    path: '/reports/services',
                    element: <Suspense><ServicesReport /></Suspense>,
                },
                {
                    path: '/reports/profit-and-loss',
                    element: <Suspense><ProfitAndLoss /></Suspense>,
                },
                {
                    path: '/reports/forecast',
                    element: <Suspense><ForecastReport /></Suspense>,
                },
                {
                    path: '/reports/website',
                    element: <Suspense><WebsiteReport /></Suspense>,
                },
                {
                    path: '/reports/recurring-revenue',
                    element: <Suspense><RecurringRevenueReport /></Suspense>,
                },
                {
                    path: '/reports/time-tracking',
                    element: <Suspense><TimeTrackingReport /></Suspense>,
                },
                {
                    path: '/reports/customer-growth',
                    element: <Suspense><CustomerGrowthReport /></Suspense>,
                },
                {
                    path: '/exports',
                    element: <Suspense><Exports /></Suspense>,
                },
                {
                    path: '/hmrc/*',
                    element: <Suspense><HMRC /></Suspense>,
                },
                {
                    path: '/:integration/oauth/authorize',
                    element: <Suspense><IntegrationOAuth /></Suspense>,
                },
                {
                    path: '/whats-new',
                    element: <Suspense><WhatsNew /></Suspense>
                },
                {
                    path: '/get-social',
                    element: <Suspense><Socials /></Suspense>
                },
                {
                    path: '/more/communication-preferences',
                    element: <Suspense><CommunicationPreferences /></Suspense>
                },
                {
                    path: '/refer-a-friend',
                    element: <Suspense><ReferAFriend /></Suspense>
                },
                {
                    path: '/redirect-short-link/:code',
                    element: <Suspense><RedirectShortLink /></Suspense>,
                },
                {
                    path: '/feature-suggestions',
                    element: <Suspense><Roadmap /></Suspense>,
                },
                {
                    path: '/admin',
                    element: <Suspense><Admin /></Suspense>,
                },
                {
                    path: '/admin/businesses',
                    element: <Suspense><Businesses /></Suspense>,
                },
                {
                    path: '/admin/revenue',
                    element: <Suspense><Revenue /></Suspense>,
                },
                {
                    path: '/admin/dial/:number',
                    element: <Suspense><Dial /></Suspense>,
                },
                {
                    path: '/admin/dial',
                    element: <Suspense><Dial /></Suspense>,
                },
                {
                    path: '/admin/experiments',
                    element: <Suspense><Experiments /></Suspense>,
                },
                {
                    path: '/admin/scratch-pad',
                    element: <Suspense><ScratchPad /></Suspense>,
                },
                {
                    path: '/admin/conversations',
                    element: <Suspense><Conversations /></Suspense>,
                },
                {
                    path: '/admin/conversations/:conversationId',
                    element: <Suspense><ConversationsRouter /></Suspense>,
                },
                {
                    path: '/admin/follow-ups',
                    element: <Suspense><FollowUps /></Suspense>,
                },
                {
                    path: '/admin/assets',
                    element: <Suspense><Assets /></Suspense>,
                },
                {
                    path: '/admin/assistant',
                    element: <Suspense><Assistant /></Suspense>,
                },
                {
                    path: '/admin/reports',
                    element: <Suspense><AdminReports /></Suspense>,
                },
                {
                    path: '/admin/risk-tool-accuracy',
                    element: <Suspense><RiskAssessmentAccuracy /></Suspense>,
                },
                {
                    path: '/admin/lessons',
                    element: <Suspense><Lessons /></Suspense>,
                },
                {
                    path: '/admin/feedback',
                    element: <Suspense><Feedback /></Suspense>,
                },
                {
                    path: '/admin/fraudulent-risk-payments',
                    element: <Suspense><FraudulentRiskPayments /></Suspense>,
                },
                {
                    path: '/admin/templates',
                    element: <Suspense><AdminTemplates /></Suspense>,
                },
                {
                    path: '/admin/shipments',
                    element: <Suspense><Shipments /></Suspense>,
                },
                {
                    path: '/admin/questionnaires',
                    element: <Suspense><Questionnaires /></Suspense>,
                },
                {
                    path: '/admin/questionnaires/:questionnaireId',
                    element: <Suspense><QuestionnaireView /></Suspense>,
                },
                {
                    path: '/admin/perks',
                    element: <Suspense><AdminPerks /></Suspense>,
                },
                {
                    path: '/admin/perks/given',
                    element: <Suspense><AdminPerksGiven /></Suspense>,
                },
                {
                    path: '/logout',
                    element: <Logout />,
                },
                // Legacy redirects
                ...redirects({
                    '/balance': '/billing',
                    '/business/brand': '/business/details',
                    '/business/socials': '/business/details',
                    '/calendar': '/plan/calendar',
                    '/customers': '/manage/customers',
                    '/customers/:id': '/manage/customers/:id',
                    '/expenses': '/manage/expenses',
                    '/marketplace': '/plan/leads',
                    '/marketplace/lead/:id': '/plan/leads/:id',
                    '/more/reports': '/reports',
                    '/payments': '/manage/payments',
                    '/payments/view/:id': '/manage/payments/:id',
                    '/quotes': '/manage/quotes',
                    '/quotes/:id': '/manage/quotes/:id',
                    '/rounds': '/plan/rounds',
                    '/services': '/business/services',
                    '/settings/customers/notifications': '/settings/customer-notifications',
                    '/settings/payments/connected-banks': '/settings/payments/bank-reconciliation',
                    '/settings/payments/connected-banks/:connectedBankId': '/settings/payments/bank-reconciliation',
                    '/settings/pricing/categories': '/business/services',
                }),
                {
                    path: '*',
                    element: <Navigate to='/' />,
                },
                // {
                //     path: '/customers/:customerId',
                //     element: <Redirect to='/manage/customers/' />,
                // }
            ]
        },
    ])
}

const redirects = (redirs) => {
    return Object.keys(redirs).map(from => {
        if (from.indexOf(':id') > -1) {
            return {
                path: from,
                element: <HandleRedirect to={redirs[from]} />,
            };
        }
        return {
            path: from,
            element: <Navigate to={redirs[from]} />,
        };
    });
}

const HandleRedirect = ({to}) => {
    const {id} = useParams();

    return <Navigate to={to.replace(':id', id)} />
}

const makeGuestRouter = (hasToken) => {
    return createBrowserRouter([
        {
            path: '/',
            element: <Guest />,
            children: [
                {
                    index: true,
                    element: hasToken ? <Authenticating /> : <Navigate to='/login' />,
                },
                {
                    path: '/login',
                    element: <Login />,
                },
                {
                    path: '/forgotten-password',
                    element: <ForgotPassword />,
                },
                {
                    path: '/forgotten-password/reset/:hash',
                    element: <ResetPassword />,
                },
                {
                    path: '/register/:code',
                    element: <Register />,
                },
                {
                    path: '/register',
                    element: <Register />,
                },
                {
                    path: '/:integration/oauth/authorize',
                    element: <IntegrationOAuthRedirect />,
                },
                {
                    path: '*',
                    element: hasToken ? <Authenticating /> : <Navigate to='/login' />,
                }
            ]
        },
    ])
}

const IntegrationOAuthRedirect = () => {
    if (window.location.search) {
        window.sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
    }

    return <Login />;
}
